import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { getDirections } from '../../../services/directionsService';

// Create vehicle icon SVG
const createVehicleIcon = (color = '#1F2937') => {
  const svgIcon = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <g>
        <ellipse cx="20" cy="35" rx="16" ry="3" fill="#000000" opacity="0.2"/>
        <rect x="8" y="15" width="24" height="15" rx="3" fill="${color}" stroke="#FFFFFF" stroke-width="2"/>
        <rect x="10" y="10" width="20" height="8" rx="4" fill="${color}" stroke="#FFFFFF" stroke-width="2"/>
        <circle cx="13" cy="30" r="4" fill="#374151" stroke="#FFFFFF" stroke-width="2"/>
        <circle cx="27" cy="30" r="4" fill="#374151" stroke="#FFFFFF" stroke-width="2"/>
        <rect x="12" y="12" width="16" height="6" rx="2" fill="#E5E7EB" stroke="#D1D5DB" stroke-width="1"/>
        <rect x="14" y="14" width="12" height="3" rx="1" fill="#F3F4F6"/>
        <circle cx="11" cy="14" r="1.5" fill="#FEF3C7" stroke="#F59E0B" stroke-width="0.5"/>
        <circle cx="29" cy="14" r="1.5" fill="#FEF3C7" stroke="#F59E0B" stroke-width="0.5"/>
        <polygon points="20,5 23,10 17,10" fill="#3B82F6" stroke="#FFFFFF" stroke-width="1"/>
      </g>
    </svg>
  `)}`;
  
  if (!window.google?.maps) return { url: svgIcon };   // Maps not loaded yet — skip Size/Point
  return {
    url: svgIcon,
    scaledSize: new window.google.maps.Size(40, 40),
    anchor: new window.google.maps.Point(20, 20)
  };
};

const mapOptions = {
  disableDefaultUI: true,
  clickableIcons: false,
  keyboardShortcuts: false,
  gestureHandling: 'greedy',
  styles: [
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  ],
};

const ItineraryMap = ({ 
  googleApiKey, 
  isPhone,
  center, 
  schedule = [], // Schedule (processed itinerary steps)
  vehiclePosition, 
  currentWaypoint,
  isAccurate,
  onMapLoad,
  fleetSettings
}) => {
  const [map, setMap] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [directionsPath, setDirectionsPath] = useState([]);
  const mapRef = useRef(null);

  // Throttle: max one Directions API call per 3 minutes, bypass on waypoint change
  const DIRECTIONS_THROTTLE_MS = 3 * 60 * 1000;
  const lastDirectionsTimeRef = useRef(null);
  const lastWaypointKeyRef = useRef(null);
  const pendingDirectionsTimerRef = useRef(null);

  // Validate center has valid coordinates
  const validCenter = center &&
    typeof center.lat === 'number' &&
    typeof center.lng === 'number' &&
    !isNaN(center.lat) &&
    !isNaN(center.lng);

  // Never mount GoogleMap before the script exposes Map — prevents Maps v3 minified errors.
  const mapsRuntimeReady =
    typeof window !== 'undefined' && typeof window.google?.maps?.Map === 'function';

  // Get only the next 4 stops from schedule
  const nextStops = useMemo(() => {
    const stops = schedule.slice(0, 4);
    console.log('ItineraryMap - nextStops:', stops.length, stops.map(s => ({
      type: s.type,
      passenger: s.passengerName,
      lat: s.Latitude,
      lng: s.Longitude
    })));
    return stops;
  }, [schedule]);

  // Handle map load
  const handleMapLoad = useCallback((mapInstance) => {
    console.log('ItineraryMap loaded');
    setMap(mapInstance);
    setMapReady(true);
    mapRef.current = mapInstance;
    
    if (onMapLoad) {
      onMapLoad(mapInstance);
    }
  }, [onMapLoad]);

  // Fetch directions via OSRM (if configured) or Google Directions fallback (throttled to once per 3 minutes)
  useEffect(() => {
    if (!map || !mapReady) return;
    if (!vehiclePosition || !vehiclePosition.lat || !vehiclePosition.lng) return;
    if (!nextStops || nextStops.length === 0) return;

    const validStops = nextStops.filter(step => step.Latitude && step.Longitude);
    if (validStops.length === 0) return;

    // Derive a stable key from the first upcoming stop to detect waypoint changes
    const firstStop = validStops[0];
    const currentWaypointKey = firstStop.StepId || firstStop.id || `${firstStop.Latitude}_${firstStop.Longitude}`;
    const waypointChanged = currentWaypointKey !== lastWaypointKeyRef.current;

    const now = Date.now();
    const elapsed = lastDirectionsTimeRef.current ? (now - lastDirectionsTimeRef.current) : Infinity;
    const throttleElapsed = elapsed >= DIRECTIONS_THROTTLE_MS;

    // If waypoint hasn't changed and throttle hasn't elapsed, defer the request
    if (!waypointChanged && !throttleElapsed) {
      if (pendingDirectionsTimerRef.current) clearTimeout(pendingDirectionsTimerRef.current);
      const remaining = DIRECTIONS_THROTTLE_MS - elapsed;
      pendingDirectionsTimerRef.current = setTimeout(() => {
        lastDirectionsTimeRef.current = null;
        setDirectionsPath(prev => [...prev]);
      }, remaining);
      return;
    }

    // Clear any pending deferred timer since we're proceeding now
    if (pendingDirectionsTimerRef.current) {
      clearTimeout(pendingDirectionsTimerRef.current);
      pendingDirectionsTimerRef.current = null;
    }

    // Build origin, destination, and intermediate waypoints
    const origin = { lat: vehiclePosition.lat, lng: vehiclePosition.lng };
    const lastStop = validStops[validStops.length - 1];
    const destination = {
      lat: parseFloat(lastStop.Latitude),
      lng: parseFloat(lastStop.Longitude)
    };
    const intermediateWaypoints = validStops.slice(0, -1).map(stop => ({
      lat: parseFloat(stop.Latitude),
      lng: parseFloat(stop.Longitude)
    }));

    const osrmUrl = fleetSettings?.osrmUrl || null;

    getDirections({ origin, destination, waypoints: intermediateWaypoints, osrmUrl })
      .then(result => {
        if (result && result.path.length > 0) {
          setDirectionsPath(result.path);
          lastDirectionsTimeRef.current = Date.now();
          lastWaypointKeyRef.current = currentWaypointKey;
        } else {
          setDirectionsPath([]);
        }
      })
      .catch(err => {
        console.error('Directions error:', err);
        setDirectionsPath([]);
      });

    // Cleanup: cancel any pending deferred timer on unmount or before next run
    return () => {
      if (pendingDirectionsTimerRef.current) {
        clearTimeout(pendingDirectionsTimerRef.current);
        pendingDirectionsTimerRef.current = null;
      }
    };
  }, [map, mapReady, vehiclePosition, nextStops, fleetSettings?.osrmUrl]);

  // Auto-fit bounds when nextStops or vehicle position changes
  useEffect(() => {
    if (!map || !mapReady || !window.google?.maps?.LatLngBounds) return;

    try {
      const bounds = new window.google.maps.LatLngBounds();
      let hasPoints = false;

      // Add vehicle position
      if (vehiclePosition && vehiclePosition.lat && vehiclePosition.lng) {
        bounds.extend({ lat: vehiclePosition.lat, lng: vehiclePosition.lng });
        hasPoints = true;
      }

      // Add next 4 stop locations
      if (nextStops && Array.isArray(nextStops)) {
        nextStops.forEach(step => {
          if (step.Latitude && step.Longitude) {
            bounds.extend({
              lat: parseFloat(step.Latitude), 
              lng: parseFloat(step.Longitude)
            });
            hasPoints = true;
          }
        });
      }

      // Fit bounds if we have points and bounds are valid
      if (hasPoints && !bounds.isEmpty()) {
        map.fitBounds(bounds, {
          top: 50,
          right: 50,
          bottom: 50,
          left: 50
        });
      }
    } catch (e) {
      console.error('fitBounds error:', e);
    }
  }, [map, mapReady, nextStops, vehiclePosition]);

  const polylineOptions = useMemo(() => ({
    strokeColor: '#3B82F6',
    strokeOpacity: 0.6,
    strokeWeight: 3,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    zIndex: 1,
  }), []);

  // Don't render GoogleMap until we have a valid center and the script is ready
  if (!validCenter || !mapsRuntimeReady) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6' }}>
        <span style={{ color: '#6b7280' }}>Loading map...</span>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={{
        width: '100%',
        height: '100%',
      }}
      center={center}
      zoom={14}
      options={mapOptions}
      onLoad={handleMapLoad}
    >
      {/* Only render map elements after map is fully loaded */}
      {mapReady && (
        <>
          {/* Google Directions polyline showing actual route - render FIRST */}
          {directionsPath.length > 1 && (
            <Polyline
              path={directionsPath}
              options={polylineOptions}
            />
          )}

          {/* Vehicle marker */}
          {vehiclePosition && vehiclePosition.lat && vehiclePosition.lng && (
            <Marker
              position={vehiclePosition}
              icon={createVehicleIcon('#3B82F6')}
              title="Your Location"
              zIndex={1000}
            />
          )}
        </>
      )}

      {/* Next 4 stop markers only - render AFTER polyline */}
      {mapReady && nextStops && Array.isArray(nextStops) && nextStops.map((step, index) => {
        if (!step.Latitude || !step.Longitude) {
          console.log('ItineraryMap - Skipping stop with no coordinates:', step);
          return null;
        }

        const position = {
          lat: parseFloat(step.Latitude),
          lng: parseFloat(step.Longitude)
        };

        const isCurrentWaypoint = currentWaypoint && 
          (step.StepId === currentWaypoint.StepId || 
           step.id === currentWaypoint.id);

        // Determine marker color: orange for pickups, blue for dropoffs
        const stepType = (step.type || '').toLowerCase();
        const markerColor = (stepType === 'pickup' || stepType === 'start') ? '#ff9800' : '#2196F3';
        
        console.log(`ItineraryMap - Rendering marker ${index + 1}:`, {
          type: stepType,
          color: markerColor,
          position: position,
          passenger: step.passengerName
        });
        
        // Create smaller circular marker without numbers
        const markerIcon = {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: isCurrentWaypoint ? 10 : 8,
          fillColor: markerColor,
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        };

        return (
          <Marker
            key={step.StepId || step.id || `${step.type}_${index}`}
            position={position}
            icon={markerIcon}
            title={step.passengerName || step.address}
            zIndex={isCurrentWaypoint ? 999 : 100}
          />
        );
      })}
    </GoogleMap>
  );
};

export default ItineraryMap;
